import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign, CheckCircle2 } from "lucide-react";
import { OpportunityFormValues } from "./schemas/opportunityFormSchemas";

interface AdditionalFeesFormProps {
  namePrefix?: 'pricing_details';
  currency?: string;
}

export const AdditionalFeesForm = ({
  namePrefix = "pricing_details",
  currency = "USD",
}: AdditionalFeesFormProps) => {
  const { control, watch } = useFormContext<OpportunityFormValues>();
  
  const includeFeesInBase = watch(`${namePrefix}.include_fees_in_base`);

  return (
    <div className="space-y-4 border p-4 rounded-md bg-background/80">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="h-5 w-5 text-primary" />
        <h2 className="text-base font-semibold">Additional Fees</h2>
      </div>
      
      <FormField
        control={control}
        name={`${namePrefix}.include_fees_in_base`}
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Include fees in base price</FormLabel>
              <FormDescription>
                If checked, all fees below will be included in the base price. If unchecked, they will be itemized separately.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
      
      {!includeFeesInBase && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.program_management.amount`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Program Management Fee</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input 
                        {...field} 
                        type="number"
                        step="0.01"
                        min="0"
                        className="pl-8"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.program_management.fee_type`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Program Management Fee Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select fee type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="total">Total Program Cost</SelectItem>
                      <SelectItem value="per_event">Per Event</SelectItem>
                      <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.material_kit.amount`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Material/Kit Fee</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input 
                        {...field} 
                        type="number"
                        step="0.01"
                        min="0"
                        className="pl-8"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.material_kit.fee_type`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Material Kit Fee Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select fee type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="total">Total Program Cost</SelectItem>
                      <SelectItem value="per_event">Per Event</SelectItem>
                      <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.refreshments.amount`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Refreshments Fee</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input 
                        {...field} 
                        type="number"
                        step="0.01"
                        min="0"
                        className="pl-8"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.refreshments.fee_type`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Refreshments Fee Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select fee type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="total">Total Program Cost</SelectItem>
                      <SelectItem value="per_event">Per Event</SelectItem>
                      <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.goodies.amount`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Goodies Fee</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input 
                        {...field} 
                        type="number"
                        step="0.01"
                        min="0"
                        className="pl-8"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name={`${namePrefix}.additional_fees.goodies.fee_type`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Goodies Fee Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select fee type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="total">Total Program Cost</SelectItem>
                      <SelectItem value="per_event">Per Event</SelectItem>
                      <SelectItem value="per_volunteer">Per Volunteer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};
